import { DateFormat, getDateRangeText, getDateText } from "../../../helpers/dateHelper";
import { ILdExpenseStat, ILdExpensePerShipStat } from "../../../models/stats";
import { CsvStatisticsFilters } from "../../StatisticsPage/shared/filters";

const sanitizeValues = (values: any[]): string[] => {
    return values.map(value => {
        if (value === null || value === undefined) return "";
        if (typeof value === "number") {
            // Round to 2 decimal places if not an integer
            if (Number.isInteger(value)) {
                return String(value);
            }
            return String(parseFloat(value.toFixed(2)));
        }
        if (typeof value === "string") return value.trim();
        return String(value);
    });
};

export const getLDShipAndDockStatisticsValues = (
    stat: ILdExpenseStat,
    filters: CsvStatisticsFilters
) => {
    // Use data directly from API response
    const values = [
        getDateText(filters.from, DateFormat.CLIENT_DATE),
        getDateText(filters.to, DateFormat.CLIENT_DATE),
        stat.destName ?? "",
        stat.dockName ?? "",
        stat.shipName ?? "",
        stat.managingCompanyName ?? "",
        stat.owningCompany ?? "",
        stat.arrivalDays || 0,
        stat.layDays || 0,
        (stat.arrivalDays || 0) + (stat.layDays || 0),
    ];

    return sanitizeValues(values);
};

export const getLDExpensePerShipStatisticsValues = (
    stat: ILdExpensePerShipStat,
    filters: CsvStatisticsFilters,
    uniqueDocks: { id: string; name: string }[]
): string[] => {
    const values = [
        getDateText(filters.from, DateFormat.CLIENT_DATE),
        getDateText(filters.to, DateFormat.CLIENT_DATE),
        stat.shipName ?? "", // Vessel Name
        stat.shipEni ?? "", // ENI-number
        stat.shipMmsi ?? "", // MMSI
        stat.managingCompany ?? "",
        stat.owningCompany ?? "",
        stat.arrivalDays ?? 0, // Arrival days total
        stat.arrivalCost ?? 0, // Total costs arrival days
        stat.layDays ?? 0, // Lay days total
        stat.layCost ?? 0, // Total Costs lay days
        stat.electricityQuantity ?? 0, // Total electricity amount
        stat.electricityCost ?? 0, // Total costs electricity
        stat.waterQuantity ?? 0, // Total water amount
        stat.waterCost ?? 0, // Total costs water
        stat.wasteQuantity ?? 0, // Number of waste disposal services
        stat.wasteCost ?? 0, // Total cost waste disposal
        stat.serviceSatCount ?? 0, // Number services Saturday
        stat.serviceSunCount ?? 0, // Number services Night/Sunday/Holidays
        stat.serviceCost ?? 0, // Total costs services
        stat.wayFeeCost ?? 0, // Total costs way fee
        stat.passengerBeds ?? 0, // Beds total
        stat.totalCost ?? 0, // Total costs
        (stat.arrivalDays ?? 0) + (stat.layDays ?? 0), // Total days (arrivalDays + layDays)
    ];

    uniqueDocks.forEach(dock => {
        const arrival = stat.arrivals?.find(a => (a.dockId ?? "null") === dock.id);
        values.push(String(arrival?.count ?? 0));
    });

    return sanitizeValues(values);
};

export const getLDExpensePerShipStatisticsHeaders = (): string[] => [
    "From",
    "To",
    "Vessel Name",
    "ENI-number",
    "MMSI",
    "Managing company",
    "Owning company",
    "Arrival days total",
    "Total costs arrival days",
    "Lay days total",
    "Total Costs lay days",
    "Total electricity amount",
    "Total costs electricity",
    "Total water amount",
    "Total costs water",
    "Number of waste disposal services",
    "Total cost waste disposal",
    "Number services Saturday",
    "Number services Night/Sunday/Holidays",
    "Total costs services",
    "Total costs way fee",
    "Beds total",
    "Total costs",
    "Total days",
    // TODO: Dock1, Dock2, Dock3 columns - these need to be added to the API response
];

export const getFileName = (statisticTypeName: string, from: number | null, to: number | null) => {
    return `${statisticTypeName.replaceAll(" ", "_")}_${getDateRangeText(
        from,
        to,
        DateFormat.API_DATE,
    ).replaceAll(" ", "")}.csv`;
};
